import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  normaliseSnapshot,
  purgeCompletedTodos,
  purgeExpiredActions,
  type Snapshot,
} from './budget/repo'
import * as api from './lib/api'
import { ConflictError, OfflineError, type Account } from './lib/api'
import { cacheClear, cacheGet, cacheSet, requestPersistentStorage } from './lib/db'

type Status = 'loading' | 'anonymous' | 'ready'

interface StoreValue {
  status: Status
  account: Account | null
  snapshot: Snapshot | null
  /** True when the last server call failed — the app is readable but not writable. */
  offline: boolean
  /** Bumped every second so countdowns tick and actions expire without a reload. */
  tick: number
  login: (code: string) => Promise<boolean>
  logout: () => Promise<void>
  /** Applies a pure change to the snapshot and pushes it to the server. */
  mutate: (change: (snapshot: Snapshot) => Snapshot) => Promise<boolean>
  refresh: () => Promise<void>
  toast: string | null
  showToast: (message: string) => void
}

const StoreContext = createContext<StoreValue | null>(null)

const cacheKey = (accountId: string) => `snapshot:${accountId}`

export function StoreProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading')
  const [account, setAccount] = useState<Account | null>(null)
  const [snapshot, setSnapshotState] = useState<Snapshot | null>(null)
  const [offline, setOffline] = useState(false)
  const [tick, setTick] = useState(0)
  const [toast, setToast] = useState<string | null>(null)

  // Refs, not state: saves happen inside async callbacks, where a captured `snapshot`
  // would be whatever it was when the callback was created rather than what it is now.
  const version = useRef(0)
  const latest = useRef<Snapshot | null>(null)
  // Saves run one at a time. Firing them in parallel — which typing in a text field does —
  // means the second carries a version the server has already moved past, and the reply is
  // a conflict the user never caused.
  const queue = useRef<Promise<unknown>>(Promise.resolve())

  const setSnapshot = useCallback((next: Snapshot | null) => {
    latest.current = next
    setSnapshotState(next)
  }, [])

  const loadState = useCallback(
    async (current: Account) => {
      try {
        const { version: serverVersion, data } = await api.getState()
        version.current = serverVersion
        const next = normaliseSnapshot(data)
        setSnapshot(next)
        setOffline(false)
        void cacheSet(cacheKey(current.id), { version: serverVersion, data: next })
      } catch (error) {
        if (error instanceof OfflineError) {
          const cached = await cacheGet<{ version: number; data: Snapshot }>(cacheKey(current.id))
          if (cached) {
            version.current = cached.version
            setSnapshot(normaliseSnapshot(cached.data))
          }
          setOffline(true)
        } else {
          setAccount(null)
          setStatus('anonymous')
        }
      }
    },
    [setSnapshot]
  )

  const boot = useCallback(async () => {
    void requestPersistentStorage()
    const current = await api.me().catch(() => null)
    if (!current) {
      setStatus('anonymous')
      return
    }
    setAccount(current)
    await loadState(current)
    setStatus('ready')
  }, [loadState])

  useEffect(() => {
    void boot()
  }, [boot])

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000)
    return () => window.clearInterval(id)
  }, [])

  // A month can roll over, or the other device can change something, while this one sits
  // in the background.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && account) void loadState(account)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [account, loadState])

  useEffect(() => {
    if (!toast) return
    const id = window.setTimeout(() => setToast(null), 3500)
    return () => window.clearTimeout(id)
  }, [toast])

  const mutate = useCallback(
    (change: (snapshot: Snapshot) => Snapshot): Promise<boolean> => {
      const run = async (): Promise<boolean> => {
        const base = latest.current
        if (!base || !account) return false

        let next: Snapshot
        try {
          next = change(base)
        } catch (error) {
          setToast(error instanceof Error ? error.message : 'That did not work')
          return false
        }
        if (next === base) return true

        // Optimistic: the UI moves immediately, then the server confirms.
        setSnapshot(next)

        try {
          version.current = await api.putState(version.current, next)
          setOffline(false)
          void cacheSet(cacheKey(account.id), { version: version.current, data: next })
          return true
        } catch (error) {
          if (error instanceof ConflictError) {
            version.current = error.version
            const server = normaliseSnapshot(error.data)
            setSnapshot(server)
            void cacheSet(cacheKey(account.id), { version: error.version, data: server })
            setToast('Someone else saved first — reloaded, please try again')
            return false
          }
          // Roll back rather than leave the screen showing something that was never saved.
          setSnapshot(base)
          if (error instanceof OfflineError) {
            setOffline(true)
            setToast("You're offline — changes need a connection")
          } else {
            setToast('Could not save that')
          }
          return false
        }
      }

      const result = queue.current.then(run, run)
      queue.current = result.catch(() => undefined)
      return result
    },
    [account, setSnapshot]
  )

  // Things that expire on their own: earn actions whose window closed, and to-dos ticked
  // more than a day ago. Doing it through mutate keeps the server copy tidy too.
  useEffect(() => {
    if (!snapshot || offline) return
    const cleaned = purgeExpiredActions(snapshot) ?? purgeCompletedTodos(snapshot)
    if (cleaned) void mutate(() => cleaned)
    // Only react to those two lists changing, not every keystroke elsewhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot?.actions, snapshot?.todos, offline])

  const login = useCallback(
    async (code: string): Promise<boolean> => {
      try {
        const current = await api.login(code)
        if (!current) return false
        setAccount(current)
        await loadState(current)
        setStatus('ready')
        return true
      } catch {
        setToast('Could not reach the server')
        return false
      }
    },
    [loadState]
  )

  const logout = useCallback(async () => {
    await api.logout().catch(() => undefined)
    await cacheClear()
    version.current = 0
    setSnapshot(null)
    setAccount(null)
    setStatus('anonymous')
  }, [setSnapshot])

  const refresh = useCallback(async () => {
    if (account) await loadState(account)
  }, [account, loadState])

  const value = useMemo<StoreValue>(
    () => ({
      status,
      account,
      snapshot,
      offline,
      tick,
      login,
      logout,
      mutate,
      refresh,
      toast,
      showToast: setToast,
    }),
    [status, account, snapshot, offline, tick, login, logout, mutate, refresh, toast]
  )

  return (
    <StoreContext.Provider value={value}>
      {children}
      {toast ? <div className="toast">{toast}</div> : null}
    </StoreContext.Provider>
  )
}

export function useStore(): StoreValue {
  const value = useContext(StoreContext)
  if (!value) throw new Error('useStore must be used inside StoreProvider')
  return value
}
